/**
 * The per-locale dirty-form cache (AM-15.2 / M5-S4).
 *
 * Switching locale in the detail shell unmounts the form. Without this, typing
 * four paragraphs of Finnish, glancing at the English source and coming back
 * loses the four paragraphs — the mockup promises it does not. The cache is
 * keyed by locale, restored on re-select, and dropped on save; the
 * unsaved-changes warning fires on route exit, never on a locale switch.
 *
 * Pure record operations, so the retention rule can be proved without a form.
 */

export type LocaleDraftCache<T> = Record<string, T>;

export function putDraft<T>(
  cache: LocaleDraftCache<T>,
  locale: string,
  values: T,
): LocaleDraftCache<T> {
  return { ...cache, [locale]: values };
}

export function readDraft<T>(cache: LocaleDraftCache<T>, locale: string): T | undefined {
  return cache[locale];
}

export function clearDraft<T>(cache: LocaleDraftCache<T>, locale: string): LocaleDraftCache<T> {
  const next: LocaleDraftCache<T> = {};
  for (const key of Object.keys(cache)) {
    if (key !== locale) next[key] = cache[key];
  }
  return next;
}

/** Which locales still hold typed work — what the exit warning names. */
export function pendingLocales<T>(cache: LocaleDraftCache<T>): string[] {
  return Object.keys(cache).sort();
}

/**
 * The sentence shown when leaving the page with work in more than one copy.
 *
 * It lists the locales rather than saying "you have unsaved changes", because
 * the whole point of the cache is that the dirty copy may not be the one on
 * screen.
 */
export function unsavedWarningCopy(locales: readonly string[]): string {
  if (locales.length === 0) return "";
  if (locales.length === 1) {
    return `The ${String(locales[0])} copy has unsaved changes. Nothing autosaves.`;
  }
  return `${String(locales.length)} copies have unsaved changes (${locales.join(", ")}). Nothing autosaves.`;
}
